/**
 * useTerminal Hook
 *
 * React hook for managing terminal connection and xterm.js integration.
 */

import type { TerminalSession, TerminalSize } from "@modules/terminal-common";
import type { FitAddon } from "@xterm/addon-fit";
import type { Terminal as XTerm } from "@xterm/xterm";
import { useCallback, useEffect, useRef, useState } from "react";
import { TerminalConnection } from "./connection";
import type {
  ConnectionState,
  TerminalClientOptions,
  UseTerminalReturn,
} from "./types";
import { useStableObject } from "./useStableObject";

export interface UseTerminalOptions
  extends Omit<
    TerminalClientOptions,
    "reconnectDelayMs" | "keepaliveIntervalMs"
  > {
  /** xterm.js terminal instance (required) */
  terminal: XTerm | null;
  /** FitAddon instance for sizing (optional) */
  fitAddon?: FitAddon | null;
}

export function useTerminal(options: UseTerminalOptions): UseTerminalReturn {
  const {
    url,
    terminalId: initialTerminalId,
    createOptions: rawCreateOptions,
    terminal,
    fitAddon,
    upsert,
    onData,
    onExit,
    onSession,
    onError,
    autoReconnect = true,
  } = options;

  const [connectionState, setConnectionState] =
    useState<ConnectionState>("disconnected");
  const [session, setSession] = useState<TerminalSession | null>(null);

  const connectionRef = useRef<TerminalConnection | null>(null);
  const terminalDataListenerRef = useRef<{ dispose: () => void } | null>(null);

  // Store callbacks in refs to avoid re-creating connection on callback changes
  const onDataRef = useRef(onData);
  const onExitRef = useRef(onExit);
  const onSessionRef = useRef(onSession);
  const onErrorRef = useRef(onError);

  // Update refs when callbacks change
  useEffect(() => {
    onDataRef.current = onData;
  }, [onData]);
  useEffect(() => {
    onExitRef.current = onExit;
  }, [onExit]);
  useEffect(() => {
    onSessionRef.current = onSession;
  }, [onSession]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const createOptions = useStableObject(rawCreateOptions);

  // Get current terminal size from fitAddon or terminal
  const getTerminalSize = useCallback((): TerminalSize => {
    if (fitAddon) {
      const dims = fitAddon.proposeDimensions();
      if (dims) {
        return { cols: dims.cols, rows: dims.rows };
      }
    }
    if (terminal) {
      return { cols: terminal.cols, rows: terminal.rows };
    }
    return { cols: 80, rows: 24 };
  }, [fitAddon, terminal]);

  // Write to terminal
  const write = useCallback((data: string | Uint8Array) => {
    connectionRef.current?.write(data);
  }, []);

  // Resize terminal
  const resize = useCallback((size: TerminalSize) => {
    connectionRef.current?.resize(size);
  }, []);

  // Disconnect from terminal
  const disconnect = useCallback(() => {
    connectionRef.current?.disconnect();
  }, []);

  // Reconnect to terminal
  const reconnect = useCallback(() => {
    connectionRef.current?.connect();
  }, []);

  // Set up connection when terminal is ready
  useEffect(() => {
    if (!terminal || !fitAddon) return;

    // Clean up existing connection
    if (connectionRef.current) {
      connectionRef.current.dispose();
    }

    // Get initial size
    const initialSize = getTerminalSize();

    // Create new connection
    const connection = new TerminalConnection({
      url,
      terminalId: initialTerminalId,
      createOptions: {
        ...createOptions,
        size: initialSize,
      },
      upsert,
      onData: (data, isFirstMessage) => {
        terminal.write(data);
        // Also call external onData if provided
        onDataRef.current?.(data, isFirstMessage);
      },
      onExit: (exitCode) => {
        onExitRef.current?.(exitCode);
      },
      onConnectionChange: setConnectionState,
      onSession: (sess) => {
        setSession(sess);
        onSessionRef.current?.(sess);
      },
      onError: (error) => {
        onErrorRef.current?.(error);
      },
      onReconnecting: () => {
        // Reset terminal before scrollback replay on reconnect
        // Using reset() instead of clear() to properly reset cursor position
        terminal.reset();
      },
      autoReconnect,
    });

    connectionRef.current = connection;

    // Set up terminal input listener
    terminalDataListenerRef.current = terminal.onData((data) => {
      connection.write(data);
    });

    // Connect
    connection.connect();

    return () => {
      terminalDataListenerRef.current?.dispose();
      terminalDataListenerRef.current = null;
      connection.dispose();
      connectionRef.current = null;
    };
    // Note: callbacks are stored in refs and not included as dependencies
  }, [
    url,
    terminal,
    fitAddon,
    initialTerminalId,
    createOptions,
    upsert,
    getTerminalSize,
    autoReconnect,
  ]);

  return {
    connectionState,
    session,
    write,
    resize,
    disconnect,
    reconnect,
  };
}
